import React, { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Box, Typography } from '@mui/material';
import { AuthService } from '../../api/AuthService';
import { Spinner } from '../../common/components/Spinner';
import { catchCallback } from '../../api/request';
import { AlTextField } from '../../common/components/AlTextField';
import { PageTitle } from '../../common/components/PageTitle';
import { useDictionary } from '../../common/contexts/DictionaryContext';
import { ConsentNote } from './ConsentNote/ConsentNote';
import { AUTH_MODE } from './AuthDialog/AuthDialog';
import { logAmplitudeEvent } from '../../common/services/amplitudeService';

const STATE = {
    LOADING: 'loading',
    TOKEN_INVALID: 'tokenInvalid',
    TOKEN_VALID: 'tokenValid',
    PASSWORD_SETTING: 'passwordSetting',
    PASSWORD_SET: 'passwordSet',
};

const formDataReducer = (formData, action) => {
    if (typeof action === 'string') {
        return { ...formData, touched: { ...formData.touched, [action]: true } };
    }
    const { name, value } = action;
    return { ...formData, [name]: value };
};

const ResetPage = ({ notifications, setUser, openAuthDialog }) => {
    const { token } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { salutations = [], titles = [] } = useDictionary();

    const [state, setState] = useState(STATE.LOADING);
    // Spec §5.3: true only for a sign-up token (GET /auth/reset → profileRequired).
    const [profileRequired, setProfileRequired] = useState(false);
    const busy = state === STATE.PASSWORD_SETTING;

    const [formData, setFormData] = useReducer(formDataReducer, {
        password: '',
        passwordRepeat: '',
        salutationId: '',
        titleId: '',
        firstName: '',
        lastName: '',
        touched: { password: false, passwordRepeat: false, firstName: false, lastName: false },
    });
    const onChange = (e) => {
        const { name, value } = e.target;
        setFormData({ name, value });
    };
    const onTitleChange = (id) => (e, checked) => {
        setFormData({ name: 'titleId', value: checked ? id : '' });
    };
    const passwordsValid = Boolean(formData.password) && formData.password === formData.passwordRepeat;
    const profileValid =
        !profileRequired ||
        (Boolean(formData.salutationId) && Boolean(formData.firstName.trim()) && Boolean(formData.lastName.trim()));
    const isValid = passwordsValid && profileValid;
    const isFormDataError = Boolean(!passwordsValid && formData.touched.password && formData.touched.passwordRepeat);
    const firstNameError = formData.touched.firstName && !formData.firstName.trim();
    const lastNameError = formData.touched.lastName && !formData.lastName.trim();

    const checkResetToken = () => {
        const request = AuthService.checkResetToken(token);
        request.then(
            (body) => {
                setProfileRequired(Boolean(body && body.profileRequired));
                setState(STATE.TOKEN_VALID);
            },
            catchCallback(() => setState(STATE.TOKEN_INVALID)),
        );
        return () => request.cancel();
    };

    const onSetPassword = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setState(STATE.PASSWORD_SETTING);

        const data = { password: formData.password };
        if (profileRequired) {
            data.salutationId = Number(formData.salutationId);
            data.titleId = formData.titleId || null;
            data.firstName = formData.firstName.trim();
            data.lastName = formData.lastName.trim();
        }
        const request = AuthService.setNewPassword(data, token);
        request.then(
            (response) => {
                // Only a sign-up token (modal flow) is recognisable here; the booking-checkbox
                // activation shares its token purpose with the legacy activation mails.
                if (profileRequired) logAmplitudeEvent('Signup: Completed', { method: 'modal' });
                // Spec decision 5: a customer response carries the login payload → log in.
                if (response && response.token) {
                    setUser(response);
                    navigate('/booking', { replace: true });
                    return;
                }
                setState(STATE.PASSWORD_SET);
            },
            catchCallback((error) => {
                notifications.showError(error.response?.data);
                setState(STATE.TOKEN_VALID);
            }),
        );
    };

    useEffect(checkResetToken, []);

    const content = (() => {
        switch (state) {
            case STATE.LOADING:
                return <Spinner />;
            case STATE.TOKEN_INVALID:
                return (
                    <Box sx={{ textAlign: 'center' }}>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {t('error.ResetTokenNotValid')}
                        </Alert>
                        <Button variant="contained" color="secondary" onClick={() => openAuthDialog(AUTH_MODE.RESET)}>
                            {t('resetForm.requestNewLink')}
                        </Button>
                    </Box>
                );
            case STATE.TOKEN_VALID:
            case STATE.PASSWORD_SETTING:
                return (
                    <Box
                        component="form"
                        sx={{ width: '400px', textAlign: 'right' }}
                        noValidate
                        autoComplete="off"
                        onSubmit={onSetPassword}
                    >
                        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                            {t(profileRequired ? 'resetForm.completeProfile' : 'resetForm.setNewPassword')}
                        </Typography>
                        {profileRequired && (
                            <>
                                <RadioGroup
                                    name="salutationId"
                                    value={formData.salutationId}
                                    onChange={onChange}
                                    row
                                    sx={{ mb: 1 }}
                                >
                                    {salutations.map((item) => (
                                        <FormControlLabel
                                            key={item.id}
                                            value={String(item.id)}
                                            control={<Radio />}
                                            disabled={busy}
                                            label={<Typography>{t(`salutation.${item.name}`)}</Typography>}
                                        />
                                    ))}
                                </RadioGroup>
                                <Box sx={{ mb: 1 }}>
                                    {titles.map((item) => (
                                        <FormControlLabel
                                            key={item.id}
                                            control={
                                                <Checkbox
                                                    checked={formData.titleId === item.id}
                                                    onChange={onTitleChange(item.id)}
                                                    disabled={busy}
                                                />
                                            }
                                            label={<Typography>{t(`title.${item.name}`)}</Typography>}
                                        />
                                    ))}
                                </Box>
                                <AlTextField
                                    fullWidth
                                    placeholder={t('placeholder.firstName')}
                                    onChange={onChange}
                                    name="firstName"
                                    onBlur={() => setFormData('firstName')}
                                    error={firstNameError}
                                    helperText={firstNameError ? t('error.required') : undefined}
                                    value={formData.firstName}
                                    required={true}
                                    sx={{ mb: 1.5 }}
                                    disabled={busy}
                                    slotProps={{ htmlInput: { autoComplete: 'given-name' } }}
                                />
                                <AlTextField
                                    fullWidth
                                    placeholder={t('placeholder.lastName')}
                                    onChange={onChange}
                                    name="lastName"
                                    onBlur={() => setFormData('lastName')}
                                    error={lastNameError}
                                    helperText={lastNameError ? t('error.required') : undefined}
                                    value={formData.lastName}
                                    required={true}
                                    sx={{ mb: 1.5 }}
                                    disabled={busy}
                                    slotProps={{ htmlInput: { autoComplete: 'family-name' } }}
                                />
                            </>
                        )}
                        <AlTextField
                            fullWidth
                            autoFocus={!profileRequired}
                            placeholder={t('placeholder.password')}
                            alTooltip={t('tooltip.resetForm.password')}
                            onChange={onChange}
                            name="password"
                            onBlur={() => setFormData('password')}
                            error={isFormDataError}
                            value={formData.password}
                            required={true}
                            sx={{ mb: 1.5 }}
                            disabled={busy}
                            type="password"
                            slotProps={{ htmlInput: { autoComplete: 'new-password' } }}
                        />
                        <AlTextField
                            fullWidth
                            placeholder={t('placeholder.passwordRepeat')}
                            alTooltip={t('tooltip.resetForm.passwordRepeat')}
                            onChange={onChange}
                            name="passwordRepeat"
                            onBlur={() => setFormData('passwordRepeat')}
                            error={isFormDataError}
                            helperText={isFormDataError ? t('error.passwordMismatch') : undefined}
                            value={formData.passwordRepeat}
                            required={true}
                            sx={{ mb: 2 }}
                            disabled={busy}
                            type="password"
                            slotProps={{ htmlInput: { autoComplete: 'new-password' } }}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            size="large"
                            disabled={busy || !isValid}
                        >
                            {t(profileRequired ? 'signupForm.finish' : 'button.set')}
                        </Button>
                        {profileRequired && <ConsentNote actionKey="signupForm.consentSet" sx={{ mt: 2 }} />}
                    </Box>
                );
            case STATE.PASSWORD_SET:
                return <Alert severity="info">{t('resetForm.newPasswordSet')}</Alert>;
            default:
                return null;
        }
    })();

    return (
        <>
            <PageTitle sx={{ pt: 8 }}>{t(profileRequired ? 'signupForm.title' : 'resetForm.title')}</PageTitle>
            <Box
                sx={(theme) => ({
                    backgroundColor: theme.palette.grey[200],
                    marginBottom: '30px',
                    padding: '32px 16px',
                    display: 'flex',
                    justifyContent: 'center',
                })}
            >
                {content}
            </Box>
        </>
    );
};

export { ResetPage };
